import { useCallback } from "react";
import { Config } from "@kaltura/mediaspace-shared-data-kms-config/Config";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { sendButtonClickAnalytics } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalyticsContext } from "./context";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

/**
 * Custom hook to send button analytics
 */
export const useButtonAnalytics = () => {
    const config: Config = useConfig();
    const buttonAnalyticsContext = useButtonAnalyticsContext();

    /**
     * a React wrapper for sending button-click analytics events to KMS
     * @param actionName - corresponds to var2 (after adding context prefix)
     * @param buttonType - corresponds to var1
     * @param entryId - (Optional) The entry ID related to the action
     * @param applicationFeature - (Optional) The application feature associated with the action
     * @param location - (Optional) The module name where the action occurred, corresponds to var4/buttonInfo
     * @param overrideLocations - (Optional) - should we override locations instead of concat them
     * @param ks - (Optional)  ks to use for analytics call (this is the weak ks which encapsulates the user id)
     */
    return useCallback(
        (
            actionName: string,
            buttonType: ButtonClickAnalyticsType,
            entryId?: string,
            applicationFeature?: string,
            location?: string,
            overrideLocations?: boolean,
            ks?: string
        ) => {
            const analyticsContext = buttonAnalyticsContext.namePrefix
                ? buttonAnalyticsContext.namePrefix
                : config.analytics.analyticsContext;
            const prefixedActionName = `${analyticsContext ? analyticsContext + " " : ""}${actionName}`;

            // set location if exists, or concat to current one
            const fullLocation = location
                ? location
                  + (!overrideLocations && buttonAnalyticsContext.location
                      ? ` (${buttonAnalyticsContext.location})`
                      : "")
                : buttonAnalyticsContext.location ?? config.analytics.moduleName;

            sendButtonClickAnalytics(
                prefixedActionName, // var2
                config.analytics.pageType, // var3
                buttonType, // var1
                entryId ?? config.analytics.entryId,
                fullLocation, // var4/buttonInfo
                applicationFeature ?? buttonAnalyticsContext.applicationFeature,
                ks
            );
        },
        [config, buttonAnalyticsContext]
    );
};
